import { defineComponent as _defineComponent } from 'vue'
import MyProjectsSection from '@/views/my-projects/components/MyProjectsSection.vue';
import InvoiceButtons from '@/views/my-projects/components/invoice/InvoiceButtons.vue';
import InvoiceAddressData from '@/views/my-projects/components/invoice/addressdata/InvoiceAddressData.vue';
import InvoicePaymentData from '@/views/my-projects/components/invoice/paymentdata/InvoicePaymentData.vue';
import InvoiceBasisData from '@/views/my-projects/components/invoice/basisdata/InvoiceBasisData.vue';
import InvoiceTexts from '@/views/my-projects/components/invoice/texts/InvoiceTexts.vue';
import InvoicePositions from '@/views/my-projects/components/invoice/positions/InvoicePositions.vue';
import InvoiceAttachments from '@/views/my-projects/components/invoice/attachments/InvoiceAttachments.vue';
import MyProjectInvoiceConfirms from '@/views/my-projects/pages/MyProjectInvoiceConfirms.vue';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import {
  computed, onBeforeUnmount, onMounted, watch
} from 'vue';
import useMessengerState from '@/state/messenger/messengerState';
import { logError } from '@/utils/logger';
import InvoicesActions from '@/views/my-projects/components/invoices/action-box/InvoicesActions.vue';
import useUser from '@/mixins/useUser';
import {
  InvoiceEventPayload, InvoiceStatus, InvoiceViolation, TypeOfPopUp
} from '@/models/Invoice';
import { useRoute } from 'vue2-helpers/vue-router';
import useIsLeavingDirty from '@/utils/isLeavingDirty';
import { whenever } from '@vueuse/core';
import usePopupState from '@/state/usePopupState';
import useI18n from '@/mixins/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsInvoice',
  setup(__props) {

const {
  invoice,
  invoices,
  fetchInvoice,
  events,
  fetchViewInvoice,
  errorMessagesPositions,
  errorMessages,
  errorMessagesAddressDataReceiver,
  errorMessagesAddressDataSender,
  invoiceId,
  isSomethingUnsaved,
  askInvoice,
  saveUnsavedChanges,
  unsavedAreas,
  waitForDebounceEnd,
  fetchUpdateReadDateInvoice,
  fetchCheckInvoice
} = useInvoice();
const { tUserFallback: t } = useUser('self');
const { t: tUseI18n } = useI18n();
const {
  createNewChat,
  isOpened
} = useMessengerState();
const route = useRoute();
const { isDirty, isLeavingDirty, onConfirm } = useIsLeavingDirty();
const { setPdf, showPopup } = usePopupState();

const statusLabel = computed(() => (invoice.value?.status ? `${t(`invoice.status-options.${invoice.value.status}`)}` : '-'));
const bcReferenceId = computed(() => (invoice.value?.bcReferenceId || '-'));

const onEvent = (event: TypeOfPopUp) => {
  events.emit(event, { invoice: invoice.value });
};

async function handleActionWithUnsavedCheck() {
  if (!isSomethingUnsaved.value) return;

  onEvent('unsaved-data');
  await askInvoice();

  if (saveUnsavedChanges.value === 'save' && isSomethingUnsaved.value) {
    onEvent('unsaved-data-failed');
  }
}

function resetSaveUnsavedChanges() {
  saveUnsavedChanges.value = 'doNotSave';
}

const onSent = async () => {
  try {
    await waitForDebounceEnd();
    resetSaveUnsavedChanges();
    await handleActionWithUnsavedCheck();
    if (saveUnsavedChanges.value === 'cancel') return;
    await fetchCheckInvoice().then(async (data: InvoiceViolation[]) => {
      const areMissingMandatoryField = data.some((violationObj) => violationObj.reason === 'MANDATORY_CHECK');
      if (areMissingMandatoryField) {
        events.emit('invoice-missing-fields', { invoiceViolation: data } as InvoiceEventPayload);
      } else {
        events.emit('send-invoice', { invoice: invoice.value });
      }
    });
  } finally {
    saveUnsavedChanges.value = 'doNotSave';
  }
};

async function loadData() {
  const foundInvoice = invoices.value.find((inv) => inv.invoiceId === invoiceId.value);
  if (!foundInvoice || foundInvoice.status === InvoiceStatus.Draft) await fetchInvoice();
  else invoice.value = foundInvoice;
  await fetchUpdateReadDateInvoice(0, invoiceId.value, { readDate: new Date().toISOString() });
}

function resetComponent() {
  invoice.value = null;
  errorMessages.value = {};
  errorMessagesAddressDataReceiver.value = {};
  errorMessagesAddressDataSender.value = {};
  errorMessagesPositions.value = [];
  unsavedAreas.value.addressData.isUnsaved = false;
  unsavedAreas.value.paymentData.isUnsaved = false;
  unsavedAreas.value.positionsData.isUnsaved = false;
}

onMounted(async () => {
  await loadData();
});

function reloadComponent() {
  resetComponent();
  loadData();
}

async function onView() {
  try {
    resetSaveUnsavedChanges();
    if (invoice.value?.status === InvoiceStatus.Draft) {
      await handleActionWithUnsavedCheck();
      if (saveUnsavedChanges.value === 'cancel') return;
    }

    const invoicePdf = invoice.value?.attachments?.find((inv) => inv.bInvoice); // Invoice PDF only available for invoices that have been sent
    const invoiceFileName = invoicePdf?.filename || `${tUseI18n('invoice.invoice-filename-preview', { bcReferenceId: invoice.value?.bcReferenceId })}`;

    showPopup();
    const attachment = await fetchViewInvoice();
    setPdf(attachment, invoiceFileName);
  } finally {
    saveUnsavedChanges.value = 'doNotSave';
  }
}

async function onChat() {
  if (invoice.value) {
    isOpened.value = true;
    try {
      await createNewChat(invoice.value.receiver.user);
    } catch (e: any) {
      logError('Error creating chat:', e);
    }
  }
}

async function checkInvoice() {
  try {
    await waitForDebounceEnd();
    resetSaveUnsavedChanges();
    await handleActionWithUnsavedCheck();
    if (saveUnsavedChanges.value === 'cancel') return;
    await fetchCheckInvoice().then(async (data: InvoiceViolation[]) => {
      if (data?.length) {
        events.emit('check-invoice', { invoiceViolation: data } as InvoiceEventPayload);
      } else {
        events.emit('check-successfully', { invoiceViolation: data } as InvoiceEventPayload);
      }
    });
  } finally {
    saveUnsavedChanges.value = 'doNotSave';
  }
}

watch(isSomethingUnsaved, (newVal) => {
  isDirty.value = newVal;
}, { immediate: true });

whenever(isLeavingDirty, async () => {
  await handleActionWithUnsavedCheck();
  if (saveUnsavedChanges.value === 'cancel') onConfirm(false);
  onConfirm(true);
});

watch(
  () => route.params.invoiceId,
  (id) => {
    if (id) reloadComponent();
  }
);

onBeforeUnmount(() => {
  resetComponent();
});

return { __sfc: true,invoice, invoices, fetchInvoice, events, fetchViewInvoice, errorMessagesPositions, errorMessages, errorMessagesAddressDataReceiver, errorMessagesAddressDataSender, invoiceId, isSomethingUnsaved, askInvoice, saveUnsavedChanges, unsavedAreas, waitForDebounceEnd, fetchUpdateReadDateInvoice, fetchCheckInvoice, t, tUseI18n, createNewChat, isOpened, route, isDirty, isLeavingDirty, onConfirm, setPdf, showPopup, statusLabel, bcReferenceId, onEvent, handleActionWithUnsavedCheck, resetSaveUnsavedChanges, onSent, loadData, resetComponent, reloadComponent, onView, onChat, checkInvoice, MyProjectsSection, InvoiceButtons, InvoiceAddressData, InvoicePaymentData, InvoiceBasisData, InvoiceTexts, InvoicePositions, InvoiceAttachments, MyProjectInvoiceConfirms, InvoicesActions }
}

})